import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  makeStyles,
  withStyles,
  Button,
  Divider as DividerMUI,
  Dialog,
  ListItem,
  List as ListMUI,
  ListItemIcon as ListItemIconMUI,
  ListItemText,
} from '@material-ui/core';
import {
  AddComment as AddCommentIcon,
  Close,
  Delete,
  FilterNone,
  FormatQuote,
  LibraryAdd as LibraryAddIcon,
  PlayCircleOutline,
} from '@material-ui/icons';

import { getLocalSettingsProxy } from 'utilities';
import ColorButtons from '../ColorButtons';
import Modal from '../Modal';
import IconButton from 'components/IconButton';
import { IconTooltip } from 'components';
import { COLOR_MAP, COLOR_PALETTES } from 'constants/appStyling/options';
import {
  AnalyticsModifiersContext,
  BookLocationValuesContext,
  DRMValuesContext,
  NetworkStatusValuesContext,
  UserAgentValuesContext,
  UserContentItemModifiersContext,
  UserContentItemValuesContext,
  UserContentValuesContext,
  ViewportValuesContext,
} from 'contexts';
import { SCREEN_SIZE_CATEGORIES } from 'constants';
import { ANNOTATION_MODALS, annotationColors } from '../../constants';
import { delay } from 'lodash';

const useStyles = makeStyles((theme) => {
  return {
    buttons: {
      display: 'flex',
      justifyContent: 'space-between',
    },
    actionButtons: {
      display: 'flex',
      alignItems: 'center',
    },
    fullScreenDialog: {
      paddingTop: theme.spacing(0.25),
      borderLeft: (props) => props.borderLeftStyle,
    },
    icon: {
      margin: '0 1rem 0 0',
      color: theme.palette.greyBlue[600],
    },
    italic: {
      fontStyle: 'italic',
    },
    listItemIcon: {
      alignItems: 'center',
    },
    screenReaderOnly: {
      clip: 'rect(1px, 1px, 1px, 1px)',
      position: 'absolute !important',
      height: '1px',
      width: '1px',
      overflow: 'hidden',
    },
    selectedButton: {
      // not in theme
      backgroundColor: '#F5F5F5',
    },
    toolbarIconButton: {
      order: 2,
    },
  };
});

const ListItemMUI = withStyles((theme) => {
  return {
    root: {
      padding: 0,
    },
  };
})(ListItem);

const ListItemTextMUI = withStyles((theme) => {
  return {
    root: {
      color: theme.palette.greyBlue[700],
    },
  };
})(ListItemText);

const ButtonMUI = withStyles((theme) => {
  return {
    root: {
      display: 'flex',
      width: '100%',
      justifyContent: 'start',
      padding: theme.spacing(1, 2),
      borderRadius: '0',

      '&.Mui-focusVisible': {
        backgroundColor: 'rgba(0, 0, 0, 0.08)',
      },
    },
  };
})(Button);

const ListItemComponent = ({
  additionalItems,
  children,
  className = '',
  isSelected,
  onClick,
  setModalOptionToFocus = () => {},
  shouldFocus = false,
}) => {
  const classes = useStyles();
  const buttonRef = useRef(null);

  useEffect(
    function focusButton() {
      if (shouldFocus && buttonRef.current) {
        buttonRef.current.focus();
        setModalOptionToFocus(null);
      }
    },
    [shouldFocus, setModalOptionToFocus],
  );

  return (
    <ListItemMUI className={className}>
      <ButtonMUI
        ref={buttonRef}
        className={isSelected ? classes.selectedButton : null}
        onClick={onClick}
        disableRipple
        // Include these aria attributes only when additional items are present.
        // This is best practices for expandable content. https://www.w3.org/WAI/ARIA/apg/patterns/disclosure/examples/disclosure-faq/
        {...(additionalItems
          ? { 'aria-controls': 'additional-items', 'aria-expanded': isSelected }
          : {})}
      >
        <ListItemIconMUI className={classes.listItemIcon}>
          {children}
        </ListItemIconMUI>
      </ButtonMUI>
      {additionalItems ? (
        <div id="additional-items">{additionalItems}</div>
      ) : null}
    </ListItemMUI>
  );
};

const ModalContent = ({
  color,
  highlight,
  highlightToFocusRef,
  modalOptionToFocus,
  onClose = () => {},
  onReadFromHere = () => {},
  setColor,
  setModalOptionToFocus,
  setUpdatePosition,
  ...props
}) => {
  const classes = useStyles();

  const { currentBookLocation } = useContext(BookLocationValuesContext);
  const { isOnline } = useContext(NetworkStatusValuesContext);
  const analytics = useContext(AnalyticsModifiersContext);

  const drmValues = useContext(DRMValuesContext);

  const userContent = useContext(UserContentValuesContext);
  const { selectedHighlight } = useContext(UserContentItemValuesContext);

  const userContentItemModifiers = useContext(UserContentItemModifiersContext);
  const {
    createCopiedTextFromSelectedHighlight,
    createFlashcardFromSelectedHighlight,
    createNoteFromSelectedHighlight,
    onDialogClose,
    onDialogOpen,
    setAriaLiveMessageForUserContent,
    setFlashcardToEdit,
    setHighlightToEdit,
    setIsCitationModalOpen,
    setNoteToEdit,
    setSelectedCopiedText,
    setSelectedHighlight,
    setSelectedNote,
  } = userContentItemModifiers;

  // Highlight Functions
  const onColorChange = async (color) => {
    analytics.highlightCreated({ color });
    // If a highlight is being created for the first time,
    // setSelectedHighlight ensures the reader sets selectedHighlight
    // to the object with the new ID
    setAriaLiveMessageForUserContent(null);
    const savedHighlight = await selectedHighlight.change({ color }).save();

    highlightToFocusRef.current = savedHighlight;
    delay(() => {
      setAriaLiveMessageForUserContent({
        action: selectedHighlight.isSaved ? 'updated' : 'created',
        color: color,
        snippet: selectedHighlight.snippet,
        type: selectedHighlight.constructor.typeName,
      });
    }, 2000);
  };

  const deleteHighlight = useCallback(
    async (highlight) => {
      await highlight.delete();
      setAriaLiveMessageForUserContent({
        action: 'deleted',
        color: highlight.color,
        snippet: highlight.snippet,
        type: highlight.constructor.typeName,
      });
    },
    [setAriaLiveMessageForUserContent],
  );

  const deleteAnnotation = useCallback(async () => {
    await deleteHighlight(highlight);
    onClose();
  }, [deleteHighlight, highlight, onClose]);

  // Note Functions
  const addNote = useCallback(() => {
    onDialogClose('annotationModal');
    onDialogOpen('noteEditor');

    const newNote = createNoteFromSelectedHighlight();

    setSelectedNote(newNote);
    setNoteToEdit(newNote);
    setSelectedHighlight(null);
  }, [
    createNoteFromSelectedHighlight,
    onDialogClose,
    onDialogOpen,
    setNoteToEdit,
    setSelectedHighlight,
    setSelectedNote,
  ]);

  const changeHighlightToNote = useCallback(async () => {
    await setHighlightToEdit(selectedHighlight);
    addNote();
  }, [selectedHighlight, setHighlightToEdit, addNote]);

  const [isSaving, setIsSaving] = useState(false);
  const onColorSelection = async (newColor) => {
    setIsSaving(true);
    setColor(newColor);
    await onColorChange(newColor);
    setIsSaving(false);
    onClose();
  };

  // Flashcard Functions
  const addFlashcard = () => {
    onDialogClose('annotationModal');
    setFlashcardToEdit(createFlashcardFromSelectedHighlight());
  };

  // Copy Functions
  const openCopyMenu = useCallback(
    (event) => {
      onDialogClose('annotationModal');

      // By default, create a new copied text record from the selected highlight.
      let copiedText = createCopiedTextFromSelectedHighlight();

      // But try to find an existing record with a matching CFI to use instead.
      if (selectedHighlight && userContent) {
        for (let existingCopiedText of userContent.copiedTextsByDocument?.get(
          currentBookLocation.document,
        ) ?? []) {
          if (existingCopiedText.cfi === selectedHighlight.cfi) {
            copiedText = existingCopiedText;
          }
        }
      }

      setSelectedCopiedText(copiedText);

      event.preventDefault();
      event.stopPropagation();

      return false;
    },
    [
      createCopiedTextFromSelectedHighlight,
      currentBookLocation.document,
      onDialogClose,
      selectedHighlight,
      setSelectedCopiedText,
      userContent,
    ],
  );

  // Citation Functions
  const showCitation = () => {
    if (selectedHighlight) {
      analytics.citationCreated();
      onDialogClose('annotationModal');
      setIsCitationModalOpen(true);
    }
  };

  const handleReadFromHere = () => {
    onDialogClose('annotationModal');
    onClose();
    onReadFromHere();
  };

  return (
    <>
      <div className={classes.buttons} {...props}>
        <div className={classes.actionButtons}>
          <ColorButtons
            handleColorSelection={onColorSelection}
            selectedColor={color}
            disabled={isSaving}
          />
          {highlight.isSaved && (
            <IconTooltip id="delete-annotation" placement="top" title="Delete">
              <IconButton
                aria-label={`Delete ${annotationColors[
                  color
                ].toLowerCase()} highlight`}
                className={classes.toolbarIconButton}
                onClick={deleteAnnotation}
              >
                <Delete />
              </IconButton>
            </IconTooltip>
          )}
        </div>
        <IconButton
          aria-label="Close"
          className={classes.toolbarIconButton}
          onClick={onClose}
        >
          <Close />
        </IconButton>
      </div>
      <DividerMUI />
      <ListMUI>
        {highlight.isSaved ? (
          <ListItemComponent onClick={changeHighlightToNote}>
            <AddCommentIcon className={classes.icon} />
            <ListItemTextMUI primary="Change to a Note" />
          </ListItemComponent>
        ) : (
          <ListItemComponent onClick={addNote}>
            <AddCommentIcon className={classes.icon} />
            <ListItemTextMUI primary="Create a Note" />
          </ListItemComponent>
        )}
        <ListItemComponent
          shouldFocus={modalOptionToFocus === ANNOTATION_MODALS.flashcard}
          setModalOptionToFocus={setModalOptionToFocus}
          onClick={addFlashcard}
        >
          <LibraryAddIcon className={classes.icon} />
          <ListItemTextMUI primary="Create a Flashcard" />
        </ListItemComponent>
        {drmValues.copyEnabled && isOnline && (
          <ListItemComponent
            shouldFocus={modalOptionToFocus === ANNOTATION_MODALS.copy}
            setModalOptionToFocus={setModalOptionToFocus}
            onClick={openCopyMenu}
          >
            <FilterNone className={classes.icon} />
            <ListItemTextMUI primary="Copy Text" />
          </ListItemComponent>
        )}
        <ListItemComponent
          shouldFocus={modalOptionToFocus === ANNOTATION_MODALS.citation}
          setModalOptionToFocus={setModalOptionToFocus}
          onClick={showCitation}
        >
          <FormatQuote className={classes.icon} />
          <ListItemTextMUI
            className={classes.listItemText}
            primary="Create a Citation"
          />
        </ListItemComponent>
        <ListItemComponent onClick={handleReadFromHere}>
          <PlayCircleOutline className={classes.icon} />
          <ListItemTextMUI
            className={classes.listItemText}
            primary="Read Aloud from Here"
          />
        </ListItemComponent>
      </ListMUI>
    </>
  );
};

const AnnotationModal = ({
  anchorPosition,
  bookLocation,
  highlight,
  highlightToFocusRef,
  modalOptionToFocus,
  onClose = () => {},
  onReadFromHere = () => {},
  setModalOptionToFocus,
  shouldShowAsDialog, // Allow override for testing
  ...props
}) => {
  const { userAgent } = useContext(UserAgentValuesContext);
  const { viewport } = useContext(ViewportValuesContext);
  const { onDialogClose, onDialogOpen } = useContext(
    UserContentItemModifiersContext,
  );

  const [localSettings] = useState(getLocalSettingsProxy);

  const [color, setColor] = useState(highlight.color);
  useEffect(
    function setHighlightColor() {
      setColor(highlight.color);
    },
    [highlight],
  );

  const handleClose = () => {
    onDialogClose('annotationModal');
    onClose();
  };

  const highlightColors = COLOR_PALETTES[localSettings.appStyling.colorPalette];
  const borderLeftColor = color ? COLOR_MAP[color] : null;
  const borderLeftStyle =
    borderLeftColor === null
      ? 'solid 4px white'
      : `solid 4px ${highlightColors[borderLeftColor].background}`;

  const classes = useStyles({
    borderLeftStyle,
  });

  shouldShowAsDialog =
    shouldShowAsDialog !== undefined // Allow override for testing
      ? shouldShowAsDialog
      : viewport.isLandscape &&
        viewport.heightCategory <= SCREEN_SIZE_CATEGORIES.PHONE &&
        userAgent.isMobile;

  return shouldShowAsDialog ? (
    <Dialog
      classes={{ paperFullScreen: classes.fullScreenDialog }}
      fullScreen
      open
      aria-labelledby="annotation-modal-dialog-title"
      TransitionProps={{
        onEnter: () => onDialogOpen('annotationModal'),
      }}
      disablePortal
      {...props}
    >
      <span
        id="annotation-modal-dialog-title"
        className={classes.screenReaderOnly}
      >
        {highlight.isSaved ? 'Manage an Annotation' : 'Create an Annotation'}
      </span>
      {
        <ModalContent
          color={color}
          highlight={highlight}
          highlightToFocusRef={highlightToFocusRef}
          modalOptionToFocus={modalOptionToFocus}
          onClose={handleClose}
          onReadFromHere={onReadFromHere}
          setColor={setColor}
          setModalOptionToFocus={setModalOptionToFocus}
        />
      }
    </Dialog>
  ) : (
    <Modal
      anchorPosition={anchorPosition}
      borderLeftColor={color}
      onClose={handleClose}
      borderLeftStyle={borderLeftStyle}
      title={
        highlight.isSaved ? 'Manage an Annotation' : 'Create an Annotation'
      }
      hideTitle
      TransitionProps={{
        onEnter: () => onDialogOpen('annotationModal'),
      }}
      {...props}
    >
      {
        <ModalContent
          color={color}
          highlight={highlight}
          highlightToFocusRef={highlightToFocusRef}
          modalOptionToFocus={modalOptionToFocus}
          onClose={handleClose}
          onReadFromHere={onReadFromHere}
          setColor={setColor}
          setModalOptionToFocus={setModalOptionToFocus}
        />
      }
    </Modal>
  );
};

AnnotationModal.propTypes = {
  anchorPosition: PropTypes.object,
  bookLocation: PropTypes.object.isRequired,
  highlight: PropTypes.object,
  highlightToFocusRef: PropTypes.object,
  modalOptionToFocus: PropTypes.string,
  onClose: PropTypes.func,
  onReadFromHere: PropTypes.func,
  setModalOptionToFocus: PropTypes.func,
  shouldShowAsDialog: PropTypes.bool,
};

export default AnnotationModal;
